import React, { useEffect, useState } from 'react';

interface Props {
  value: number | bigint | string;
  decimals: number;
  animationDuration?: number;
  updateRate?: number;
}

export const AnimatedNumber = React.memo((props: Props) => {
  const { value, decimals, animationDuration, updateRate } = props;
  const val = useAnimatedNumber({
    currentValue: value,
    decimals,
    animationDuration,
    updateRate,
  });

  return val.value;
});

function usePrevious<T>(value: T) {
  const [val, setVal] = useState<{ prev: T; current: T }>({
    prev: value,
    current: value,
  });

  useEffect(() => {
    setVal((prevState) => {
      if (prevState.current !== value) {
        return {
          prev: prevState.current,
          current: value,
        };
      }

      return prevState;
    });
  }, [value]);

  return { prev: val.prev, current: val.current };
}

interface UseAnimatedNumberParams {
  currentValue: number | bigint | string;
  decimals: number;
  animationDuration?: number;
  updateRate?: number;
}

function useAnimatedNumber(params: UseAnimatedNumberParams) {
  const {
    currentValue,
    decimals,
    animationDuration = 7000,
    updateRate = 50,
  } = params;
  const { prev, current } = usePrevious(Number(currentValue));
  const [value, setValue] = useState({
    raw: Number(currentValue),
    value: Number(currentValue),
  });

  useEffect(() => {
    if (current === prev) {
      setValue({
        raw: Number(current),
        value: Number(current),
      });
      return;
    }

    setValue({
      raw: Number(prev),
      value: Number(prev),
    });

    const diff = Number(current) - Number(prev);
    const direction = diff > 0 ? 1 : -1;
    const steps = animationDuration / updateRate;
    const stepSize = Math.abs(diff) / steps;

    let interval = 0;
    const now = Date.now();

    const tick = () => {
      const timeElapsed = Date.now() - now;

      if (timeElapsed >= animationDuration) {
        setValue({
          raw: Number(current),
          value: Number(current),
        });
        return;
      }

      setValue((prev) => {
        const newValue = prev.raw + direction * stepSize;

        if (
          (direction > 0 && newValue >= current) ||
          (direction < 0 && newValue <= current)
        ) {
          return prev;
        }

        return {
          raw: newValue,
          value: newValue,
        };
      });

      interval = window.setTimeout(tick, updateRate);
    };

    interval = window.setTimeout(tick, updateRate);
    return () => {
      window.clearTimeout(interval);
    };
  }, [animationDuration, current, decimals, prev, updateRate]);

  return { value: value.value.toFixed(decimals), raw: value.raw };
}
